<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import crmApi from 'dashboard/api/crm';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CrmOpportunityForm from './CrmOpportunityForm.vue';

const props = defineProps({
  contactId: { type: Number, required: true },
  conversationId: { type: Number, default: null },
});

const { t } = useI18n();
const opportunities = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const editingOpportunityId = ref(null);

const fetchOpportunities = async () => {
  if (!props.contactId) return;
  isLoading.value = true;
  try {
    const { data } = await crmApi.getContactOpportunities(props.contactId);
    opportunities.value = data.payload || data;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchOpportunities);

const handleFormClose = async () => {
  showForm.value = false;
  editingOpportunityId.value = null;
  await fetchOpportunities();
};

const handleEdit = (oppId, e) => {
  e.stopPropagation();
  editingOpportunityId.value = oppId;
};
</script>

<template>
  <div class="flex flex-col gap-2 px-1">
    <div class="flex justify-end">
      <button
        class="text-xs text-woot-500 hover:text-woot-600 font-medium transition-colors"
        @click="showForm = true"
      >
        + {{ t('CRM.CONTACT_OPPORTUNITIES.NEW') }}
      </button>
    </div>

    <p v-if="isLoading" class="text-xs text-n-slate-10 py-2 text-center">
      {{ t('CRM.CONTACT_OPPORTUNITIES.LOADING') }}
    </p>
    <p
      v-else-if="opportunities.length === 0"
      class="text-xs text-n-slate-10 py-2 text-center"
    >
      {{ t('CRM.CONTACT_OPPORTUNITIES.EMPTY') }}
    </p>
    <template v-else>
      <div
        v-for="opp in opportunities"
        :key="opp.id"
        class="flex flex-col gap-1.5 py-2 border-b border-n-weak last:border-0"
      >
        <div class="flex items-start justify-between gap-2">
          <span class="text-sm text-n-slate-12 font-medium leading-tight">{{
            opp.title
          }}</span>
          <div class="flex items-center gap-2 flex-shrink-0">
            <span
              v-if="opp.value_display"
              class="text-xs text-n-slate-11 font-medium"
              >{{ opp.value_display }}</span
            >
            <button
              class="text-n-slate-9 hover:text-woot-500 transition-colors"
              :title="t('CRM.PANEL.EDIT')"
              @click="handleEdit(opp.id, $event)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="flex flex-wrap gap-x-3 gap-y-0.5">
          <span v-if="opp.stage?.name" class="text-xs text-n-slate-10">{{
            opp.stage.name
          }}</span>
          <span v-if="opp.product_service" class="text-xs text-n-slate-10">
            · {{ opp.product_service }}
          </span>
        </div>

        <div v-if="opp.assigned_agent" class="flex items-center gap-1.5 mt-0.5">
          <Avatar
            :src="opp.assigned_agent.avatar_url"
            :name="opp.assigned_agent.name"
            :size="16"
            rounded-full
          />
          <span class="text-xs text-n-slate-10">{{
            opp.assigned_agent.name
          }}</span>
        </div>
      </div>
    </template>

    <CrmOpportunityForm
      v-if="showForm"
      :contact-id="contactId"
      :conversation-id="conversationId"
      @close="handleFormClose"
    />

    <CrmOpportunityForm
      v-if="editingOpportunityId"
      :key="editingOpportunityId"
      :opportunity-id="editingOpportunityId"
      @close="handleFormClose"
    />
  </div>
</template>
